import React, { useEffect, useState, useRef } from "react";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../common/hooks/hooks";
import { useGetProductsQuery } from "../../api/productApi";
import { setPagination } from "../../productSlice";
import { useSyncProductsArchiveStateToUrl } from "../../hooks/useSyncProductsArchiveStateToUrl";
import { useSyncSeoTags } from "../../hooks/useSyncSeoTags";
import { openProductModal } from "../common/ProductModal/productModalSlice";
import FilterSidebar from "./FilterSidebar/FilterSidebar";
import ProductsToolbar from "./ProductsToolbar";
import ActiveFilters from "./ActiveFilters";
import ProductsPagination from "./ProductsPagination";
import ProductsGrid from "./ProductsGrid";
import FilterSidebarOffcanvas from "./FilterSidebar/FilterSidebarOffcanvas";
import MobileToolbar from "./MobileToolbar";
import MobileCategoryStrip from "./MobileCategoryStrip";
import CategoryHeader from "./CategoryHeader";

// --- Main Container Component ---
const ProductsArchive: React.FC = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.product.filters);
  const pagination = useAppSelector((state) => state.product.pagination);
  const activeView = useAppSelector((state) => state.product.ui.activeView);

  // --- Call the synchronization hooks here ---
  // 1. Syncs Redux filters to the URL Query Params (preserves language)
  useSyncProductsArchiveStateToUrl(["lang"]);

  // 2. Syncs the document <title> tag dynamically based on the active category
  useSyncSeoTags();

  // --- Handler to open the GLOBAL Quick View Modal ---
  const handleOpenQuickView = (productId: number) => {
    dispatch(openProductModal({ productId }));
  };

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const filterToggleButtonRef = useRef<HTMLButtonElement>(null);

  const queryParams = {
    ...filters,
    page: pagination.currentPage,
    limit: pagination.perPage,
  };

  const {
    data: result,
    isLoading,
    isFetching,
    isError,
  } = useGetProductsQuery(queryParams);

  const products = result?.products;
  const paginationData = result?.pagination;

  useEffect(() => {
    if (paginationData) {
      dispatch(
        setPagination({
          totalPages: paginationData.totalPages,
          totalProducts: paginationData.totalProducts,
        }),
      );
    }
  }, [paginationData, dispatch]);

  return (
    <div className="products-archive">
      <div className="products-archive__container">
        <div className="products-archive__grid">
          <aside className="products-archive__sidebar">
            <FilterSidebar idPrefix="desktop-" />
          </aside>
          <main className={`products-archive__main view-${activeView}`}>
            <MobileToolbar
              onOpenFilters={() => setIsMobileFilterOpen(true)}
              triggerRef={filterToggleButtonRef}
            />
            <MobileCategoryStrip />

            {/* --- Dynamic SEO Category Title and Description --- */}
            <CategoryHeader />

            <ProductsToolbar isLoading={isLoading} />
            <ActiveFilters />
            <ProductsGrid
              products={products}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              onQuickViewClick={handleOpenQuickView}
            />
            <ProductsPagination isFetching={isFetching} />
          </main>
        </div>
      </div>
      <FilterSidebarOffcanvas
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        idPrefix="mobile-"
        triggerRef={filterToggleButtonRef}
      />
    </div>
  );
};

export default ProductsArchive;
